import axios, { AxiosResponse } from "axios";

import { EHttpMethods } from "../../../../enums";
import { WEBHOOK_URL } from "../../../constants";
import { getToken } from "../../../context/authContext";
import { ApiServiceManager, fetchRequest } from "../../apiServiceManager";
import { DOWNLOAD_URL, PRESIGNED_URL } from "./constants";
import {
  IDownloadPresignedUrlBody,
  IDownloadPresignedUrlResponse,
  IUploadFileOnPresignedUrlBody,
  IUploadPresignedUrlBody,
  IUploadPresignedUrlResponse,
} from "./model";

const defaultProvide = async () => await getToken();

/** Asks the webhook service for a presigned PUT URL to upload straight to the bucket. */
export const getUploadPresignedUrl = async (
  body: IUploadPresignedUrlBody
): Promise<string> => {
  const descriptor = new ApiServiceManager(
    `${WEBHOOK_URL}${PRESIGNED_URL}`,
    EHttpMethods.POST,
    defaultProvide
  );
  const response = (await fetchRequest(
    descriptor,
    body
  )) as IUploadPresignedUrlResponse;
  return response.presigned_url;
};

/** Presigned GET URL for an already-uploaded object. */
export const getDownloadPresignedUrl = async (
  body: IDownloadPresignedUrlBody
): Promise<string> => {
  const descriptor = new ApiServiceManager(
    `${WEBHOOK_URL}${DOWNLOAD_URL}`,
    EHttpMethods.POST,
    defaultProvide
  );
  const response = (await fetchRequest(
    descriptor,
    body
  )) as IDownloadPresignedUrlResponse;
  return response.presigned_url;
};

/**
 * PUTs the raw file at a presigned URL.
 *
 * Uses axios rather than the shared fetchRequest: this request must not carry our auth header
 * or a JSON content type — the signature covers the headers, so adding any would break it. The
 * empty Content-Type mirrors how the admin portal signs these uploads.
 */
export const uploadFileOnPresignedUrl = async ({
  presignedUrl,
  file,
  progressCallback,
}: IUploadFileOnPresignedUrlBody): Promise<AxiosResponse> => {
  return axios.put(presignedUrl, file, {
    headers: {
      "Content-Type": "",
    },
    onUploadProgress: progressCallback
      ? (progressEvent) => {
          progressCallback({
            loaded: progressEvent.loaded,
            total: progressEvent.total || 0,
          });
        }
      : undefined,
  });
};
